import {
  askedAt,
  askedQuestionsIn,
  closeOpenQuestions,
  type AskedQuestion,
} from '@inference.engine/agentic/human-input';
import type { FileAssetWithUrl } from '@inference.engine/api-client';
import type {
  ChatUIMessage,
  DataToolEndEvent,
  DataToolErrorEvent,
  DataToolStartEvent,
} from '../model/ai.types';

/**
 * Local mirrors of the chats-domain `Message` / `UserMessage` /
 * `AgentMessage` / `ThinkingProcess` types. Defined here so this slice does
 * NOT import from `@/features/chats` (dependency injection — cross-feature OUT clean).
 *
 * Structural compatibility ensures the consuming page can pass the result
 * of these converters into chats UI components (`ChatThread`, etc.) without
 * casts: each shape mirrors the corresponding chats type field-for-field
 * (modulo the loose `metadata` typing — chats's `ExchangeMetadataDto` is a
 * structurally-loose record).
 */
export interface ChatThinkingProcessLike {
  id: string;
  agentId: string;
  status: 'start' | 'delta' | 'end';
  text: string;
}

export interface ChatUserMessageLike {
  type: 'user';
  messageId: string;
  content: string;
  agentId?: string;
  inputs?: Record<string, unknown>;
  files?: FileAssetWithUrl[];
  metadata?: Record<string, unknown>;
}

export interface ChatAgentMessageLike {
  type: 'agent';
  messageId: string;
  content: string | { text: string };
  files: FileAssetWithUrl[];
  status?: 'complete' | 'processing' | 'queued';
  subtext?: string;
  agentId?: string;
  isStreaming?: boolean;
  thinkingProcesses?: Array<ChatThinkingProcessLike>;
  metadata?: Record<string, unknown>;
  isLastMessage?: boolean;
  /** Every tool call the reply made, in the order they started, each at its latest state. */
  toolSteps?: ToolStepEvent[];
  /** The other data parts each of those calls streamed, by call id. */
  toolStepParts?: Record<string, ToolStepPart[]>;
  /** The questions this reply asked the person, each with the answer they gave, if they did. */
  askedQuestions?: AskedQuestion[];
}

export type ChatMessageLike = ChatUserMessageLike | ChatAgentMessageLike;

/** A tool call's latest state, as the progress tree draws it. */
export type ToolStepEvent = DataToolStartEvent | DataToolEndEvent | DataToolErrorEvent;

/** A data part a tool call streamed about itself, paired with the call by carrying its id. */
export interface ToolStepPart {
  type: string;
  data: unknown;
}

function extractFilesFromAIMessage(message: ChatUIMessage): FileAssetWithUrl[] {
  const initialFileMap: Record<string, FileAssetWithUrl> = {};
  message.parts.forEach((part) => {
    if (part.type !== 'data-files') return;
    part.data.files.forEach((file) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (initialFileMap[file.assetId]) return;
      initialFileMap[file.assetId] = file;
    });
  });

  return Object.values(initialFileMap);
}

function getLastMessage(messages: ChatUIMessage[]): ChatUIMessage | undefined {
  const last = messages[messages.length - 1];
  return last;
}

function convertAssistantMessage(
  message: ChatUIMessage,
  last?: ChatUIMessage,
): ChatAgentMessageLike {
  const isLast = last ? last.id === message.id : false;
  const { content, asked } = readReply(message);
  return {
    type: 'agent',
    messageId: message.id,
    content,
    status: 'complete',
    files: extractFilesFromAIMessage(message),
    agentId: message.metadata?.agentId,
    // If metadata property is present - then it came as part of a stream, not from history
    isStreaming: message.metadata ? isLast : false,
    isLastMessage: isLast ? true : false,
    thinkingProcesses: extractThinkingProcesses(message),
    metadata: message.metadata ? { ...message.metadata } : undefined,
    ...extractToolSteps(message),
    ...(asked.length > 0 ? { askedQuestions: asked } : {}),
  };
}

const TOOL_STEP_TYPES = new Set(['data-toolStart', 'data-toolComplete', 'data-toolError']);

/**
 * The reply's tool calls, read back from the message itself. Their parts are not transient, so they
 * stay on the message once the turn ends, and the steps outlive the stream that drew them live.
 * A call's later part replaces its earlier one, and a call keeps the place it started in.
 */
function extractToolSteps(
  message: ChatUIMessage,
): Pick<ChatAgentMessageLike, 'toolSteps' | 'toolStepParts'> {
  const steps = new Map<string, ToolStepEvent>();
  const related: Record<string, ToolStepPart[]> = {};
  for (const part of message.parts) {
    if (TOOL_STEP_TYPES.has(part.type)) {
      const step = part as ToolStepEvent;
      steps.set(step.data.callId, { type: step.type, data: step.data } as ToolStepEvent);
      continue;
    }
    const { id, data } = part as { id?: unknown; data?: unknown };
    if (!part.type.startsWith('data-') || typeof id !== 'string') continue;
    (related[id] ??= []).push({ type: part.type, data });
  }
  if (steps.size === 0) return {};
  const toolStepParts = Object.fromEntries(
    Object.entries(related).filter(([callId]) => steps.has(callId)),
  );
  return { toolSteps: [...steps.values()], toolStepParts };
}

/**
 * Converts AI SDK UIMessage to our Message format
 * Simplified version that focuses on basic conversion
 */
function convertAISdkMessageToChatMessage(
  message: ChatUIMessage,
  last?: ChatUIMessage,
): ChatMessageLike {
  if (message.role === 'assistant') {
    return convertAssistantMessage(message, last);
  }

  const messageId = message.id;
  const content = extractContent(message);
  if (message.role === 'user') {
    return {
      type: 'user',
      messageId,
      content,
      files: message.metadata?.files,
      metadata: message.metadata ? { ...message.metadata } : undefined,
    };
  }

  // Fallback for unknown message types
  return {
    type: 'user',
    messageId,
    content,
    files: [],
    metadata: undefined,
  };
}

/**
 * Converts array of AI SDK messages to our Message format
 */
export function convertAISdkMessagesToChatMessages(
  aiSdkMessages: ChatUIMessage[],
): ChatMessageLike[] {
  const lastMessage = getLastMessage(aiSdkMessages);
  return withMovedOnQuestionsClosed(withoutResumedPauses(aiSdkMessages))
    .filter((message) => !isPausedWithoutReply(message))
    .map((message) => convertAISdkMessageToChatMessage(message, lastMessage));
}

/**
 * The thread without the paused halves a live resume left behind. Resumed in the session, a turn
 * answers in a message of its own carrying the same calls, settled, while the paused copy keeps
 * them waiting; closed below as moved on from, those would read as questions nobody answered. The
 * resumed message is the SDK's copy of the paused one, so whatever text came before the pause
 * goes with it rather than showing twice.
 */
function withoutResumedPauses(messages: ChatUIMessage[]): ChatUIMessage[] {
  const settled = new Set(
    messages.flatMap((message) =>
      message.parts.flatMap((part) => {
        const call = part as { toolCallId?: unknown; state?: unknown };
        return typeof call.toolCallId === 'string' && call.state !== 'approval-requested'
          ? [call.toolCallId]
          : [];
      }),
    ),
  );
  return messages.filter((message) => {
    if (message.role !== 'assistant') return true;
    const waiting = message.parts.filter(
      (part) => (part as { state?: unknown }).state === 'approval-requested',
    );
    if (waiting.length === 0) return true;
    return !waiting.every((part) =>
      settled.has((part as { toolCallId?: unknown }).toolCallId as string),
    );
  });
}

/**
 * The thread with questions the person moved on from closed as not answered. Questions still
 * waiting before the latest message the person sent are ones they sent it instead of answering,
 * and the server closed them when it arrived. Every call still waiting there was a question, a
 * tool's own as well as ask_user: the server refuses a new message while an approval waits.
 */
function withMovedOnQuestionsClosed(messages: ChatUIMessage[]): ChatUIMessage[] {
  const lastSent = messages.findLastIndex((message) => message.role === 'user');
  if (lastSent < 1) return messages;
  return [
    ...closeOpenQuestions(messages.slice(0, lastSent), () => true),
    ...messages.slice(lastSent),
  ];
}

/**
 * The half of a turn that paused on a tool approval before saying anything. It has nothing to
 * show, and drawn as an agent message it would sit as a loading bubble while the approval card
 * waits, then stay above the answer once the turn resumes into a message of its own.
 */
function isPausedWithoutReply(message: ChatUIMessage): boolean {
  if (message.role !== 'assistant') return false;
  const waiting = message.parts.some(
    (part) => (part as { state?: unknown }).state === 'approval-requested',
  );
  return waiting && extractContent(message) === '';
}

/**
 * The failure category of a turn whose answer an output guardrail rejected after it had streamed.
 * The server's notice is the turn's last text part, and stands alone.
 */
const ANSWER_WITHHELD = 'ANSWER_WITHHELD';

/**
 * Each text part is a block of its own, such as what an agent wrote before and after a tool call,
 * so they stand as paragraphs: a model ends a block without the space or break that would separate
 * it from the next. A hybrid agent's `text` arrives already projected by the server, as one part.
 */
function extractContent(message: ChatUIMessage): string {
  return readReply(message).content;
}

/**
 * The reply's content and what it asked the person, read in one pass so each call's questions are
 * placed at the length the content had reached, where they show. History keeps ask_user calls
 * like any other. A withheld answer's content is only the notice that replaced it, so what it
 * asked goes first.
 */
function readReply(message: ChatUIMessage): { content: string; asked: AskedQuestion[] } {
  const texts: string[] = [];
  const asked: AskedQuestion[] = [];
  let length = 0;
  for (const part of message.parts) {
    if (part.type === 'text' && part.text !== '') {
      length += (texts.length > 0 ? PARAGRAPH_BREAK.length : 0) + part.text.length;
      texts.push(part.text);
      continue;
    }
    const questions = askedQuestionsIn(part);
    if (questions) asked.push(...askedAt(questions, length));
  }
  if (message.metadata?.issue?.category === ANSWER_WITHHELD) {
    return { content: texts.at(-1) ?? '', asked: askedAt(asked, 0) };
  }
  return { content: texts.join(PARAGRAPH_BREAK), asked };
}

const PARAGRAPH_BREAK = '\n\n';

function extractThinkingProcesses(
  message: ChatUIMessage,
): ChatAgentMessageLike['thinkingProcesses'] {
  const thinkingProcesses = new Map<string, ChatThinkingProcessLike>();
  message.parts.forEach((part) => {
    if (part.type !== 'data-agentThinking') return;
    if (part.data.status === 'start') {
      thinkingProcesses.set(part.data.id, {
        id: part.data.id,
        agentId: part.data.agentId,
        status: 'start',
        text: '',
      });
      return;
    }

    const thought = thinkingProcesses.get(part.data.id);
    if (!thought) return;
    if (part.data.status === 'delta') {
      thought.text += part.data.textDelta;
    } else {
      thought.status = 'end';
    }
  });

  return Array.from(thinkingProcesses.values());
}

/**
 * Annotates historical messages with metadata for better tracking.
 *
 * Generic `<T extends { type: ...; metadata?: ...; agentId?: ... }>` so
 * consumers (chats's `useChatHistory`) can pass their strict-typed
 * `Message[]` and receive `Message[]` back.
 */
export function annotateHistoricalMessages<
  T extends {
    type: string;
    metadata?: Record<string, unknown> | undefined;
    agentId?: string | undefined;
  },
>(messages: T[]): T[] {
  return messages.map((message) => ({
    ...message,
    // Add metadata to distinguish historical messages
    metadata: {
      ...(message.metadata ?? {}),
      isHistorical: true,
      ...(message.type === 'agent' && 'agentId' in message ? { agentId: message.agentId } : {}),
    },
  }));
}
